import React, { useState } from 'react';
import styled from 'styled-components';

const TestContainer = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TestButton = styled.button`
  background-color: #007cba;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin: 1rem 0.5rem;
  
  &:hover {
    background-color: #005a87;
  }
`;

const ResultContainer = styled.div`
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 0.9rem;
  max-height: 400px;
  overflow-y: auto;
`;

const SuccessContainer = styled.div`
  background-color: #e8f5e8;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border-left: 4px solid #2e7d32;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border-left: 4px solid #c62828;
`;

const HeaderTest = () => {
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const testBasicAuth = async () => {
    setLoading(true);
    setError('');
    setResult('Testing Basic Authentication with Authorization Header...\n\n');
    
    try {
      const credentials = btoa('ck_8c490c6c92d07347d264eb393813e76c141f64ae:cs_f5e70b5387cf4e6e7f2549ea67fac7e949991879');
      
      const response = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wc/v3/products?per_page=1', {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json',
        },
      });

      setResult(prev => prev + `Status: ${response.status} ${response.statusText}\n`);
      
      if (response.ok) {
        const products = await response.json();
        setResult(prev => prev + `✅ Basic Auth Success! Found ${products.length} products\n`);
        setResult(prev => prev + `First product: ${products[0]?.name || 'N/A'}\n\n`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setResult(prev => prev + `❌ Basic Auth Failed: ${errorData.message || 'Unknown error'}\n\n`);
      }
    } catch (err) {
      setResult(prev => prev + `❌ Basic Auth Error: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  const testQueryAuth = async () => {
    setLoading(true);
    setError('');
    setResult('Testing Query Parameter Authentication...\n\n');
    
    try {
      const response = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wc/v3/products?per_page=1&consumer_key=ck_8c490c6c92d07347d264eb393813e76c141f64ae&consumer_secret=cs_f5e70b5387cf4e6e7f2549ea67fac7e949991879', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setResult(prev => prev + `Status: ${response.status} ${response.statusText}\n`);
      
      if (response.ok) {
        const products = await response.json();
        setResult(prev => prev + `✅ Query Auth Success! Found ${products.length} products\n`);
        setResult(prev => prev + `First product: ${products[0]?.name || 'N/A'}\n\n`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setResult(prev => prev + `❌ Query Auth Failed: ${errorData.message || 'Unknown error'}\n\n`);
      }
    } catch (err) {
      setResult(prev => prev + `❌ Query Auth Error: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  const testCORS = async () => {
    setLoading(true);
    setError('');
    setResult('Testing CORS and Headers...\n\n');
    
    try {
      // Test with CORS headers
      const response = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wc/v3/products?per_page=1&consumer_key=ck_8c490c6c92d07347d264eb393813e76c141f64ae&consumer_secret=cs_f5e70b5387cf4e6e7f2549ea67fac7e949991879', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      setResult(prev => prev + `Status: ${response.status} ${response.statusText}\n`);
      setResult(prev => prev + `CORS Headers:\n`);
      setResult(prev => prev + `- Access-Control-Allow-Origin: ${response.headers.get('Access-Control-Allow-Origin') || 'Not set'}\n`);
      setResult(prev => prev + `- Access-Control-Allow-Methods: ${response.headers.get('Access-Control-Allow-Methods') || 'Not set'}\n`);
      setResult(prev => prev + `- Access-Control-Allow-Headers: ${response.headers.get('Access-Control-Allow-Headers') || 'Not set'}\n\n`);
      
      if (response.ok) {
        const products = await response.json();
        setResult(prev => prev + `✅ CORS Test Success! Found ${products.length} products\n\n`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setResult(prev => prev + `❌ CORS Test Failed: ${errorData.message || 'Unknown error'}\n\n`);
      }
    } catch (err) {
      setResult(prev => prev + `❌ CORS Test Error: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  const testWordPressAPI = async () => {
    setLoading(true);
    setError('');
    setResult('Testing WordPress REST API (non-WooCommerce)...\n\n');
    
    try {
      const response = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wp/v2/posts?per_page=1', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setResult(prev => prev + `Status: ${response.status} ${response.statusText}\n`);
      
      if (response.ok) {
        const posts = await response.json();
        setResult(prev => prev + `✅ WordPress API Success! Found ${posts.length} posts\n`);
        setResult(prev => prev + `First post: ${posts[0]?.title?.rendered || 'N/A'}\n\n`);
      } else {
        setResult(prev => prev + `❌ WordPress API Failed\n\n`);
      }
    } catch (err) {
      setResult(prev => prev + `❌ WordPress API Error: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  const runAllTests = async () => {
    setLoading(true);
    setError('');
    setResult('Running All Tests...\n\n');
    
    await testWordPressAPI();
    await testQueryAuth();
    await testBasicAuth();
    await testCORS();
    
    setResult(prev => prev + '🎯 All Tests Complete!\n\n');
    setResult(prev => prev + 'Analysis:\n');
    setResult(prev => prev + '1. If WordPress API works but WooCommerce fails: WooCommerce configuration issue\n');
    setResult(prev => prev + '2. If Basic Auth fails but Query Auth works: Authorization header being stripped\n');
    setResult(prev => prev + '3. If both fail: API key permissions or WooCommerce not properly configured\n');
    setResult(prev => prev + '4. If CORS headers are missing: Server configuration issue\n');
    
    setLoading(false);
  };

  return (
    <TestContainer>
      <h2>WooCommerce Header & CORS Test</h2>
      <p>This page tests different authentication methods and CORS configuration to identify the root cause.</p>
      
      <div style={{ marginBottom: '2rem' }}>
        <TestButton onClick={testWordPressAPI} disabled={loading}>
          Test WordPress API
        </TestButton>
        <TestButton onClick={testQueryAuth} disabled={loading}>
          Test Query Auth
        </TestButton>
        <TestButton onClick={testBasicAuth} disabled={loading}>
          Test Basic Auth
        </TestButton>
        <TestButton onClick={testCORS} disabled={loading}>
          Test CORS
        </TestButton>
        <TestButton onClick={runAllTests} disabled={loading}>
          Run All Tests
        </TestButton>
      </div>
      
      {error && <ErrorContainer>{error}</ErrorContainer>}
      
      {result && (
        <ResultContainer>
          {result}
        </ResultContainer>
      )}
    </TestContainer>
  );
};

export default HeaderTest;